import {AfterViewInit, Directive, ElementRef, EventEmitter, Input, NgZone, Output} from "@angular/core";
import {Observable, Subject, defer, of, timer, merge} from "rxjs";
import {map, tap, retryWhen, delayWhen, takeUntil, scan, delay} from 'rxjs/operators';

/**
 *  Usage
 *

 directive that allows you to load images which are deferred (not available right away)
 so image will be polled. While image is polled we can show default image as well as
 as loading image.

 usage:

 <img lazyImage class="center-block" style="width: 229px; height: 130px"
 [url]="['https:// secure.digitalsignage.com/foo/assets/some_lazy1.png','https:// secure.digitalsignage.com/foo/assets/some_lazy2.png']"
 [loadingImage]="'https:// secure.digitalsignage.com/foo/assets/screen_loading.png'"
 [defaultImage]="'https:// secure.digitalsignage.com/foo/assets/screen.png'"
 [errorImage]="'https:// secure.digitalsignage.com/foo/assets/screen_error.png'"
 [retry]="5"
 [delay]="1500"
 (loaded)="_onLoaded()"
 (error)="_onError()"
 (completed)="_onCompleted()">

 or to use via API

 ...

 @ViewChild(LazyImage)
 lazyImage: LazyImage;

 _lazyLoad() {
     this.lazyImage.setUrl(['https: //secure.digitalsignage.com/foo/assets/some_lazy.png']);
 }

 _resetSnapshotSelection() {
    if (this.lazyImage)
         this.lazyImage.resetToDefault();
 }

 _takeSnapshot() {
     this.lazyImage.url = 'http://example.com/foo.png;
 }

 ...

 */

@Directive({
    selector: '[lazyImage]'
})
// tslint:disable-next-line
export class LazyImage implements AfterViewInit {

    private m_urls: Array<string> = [];
    private m_index = 0;
    private cancel$ = new Subject();

    constructor(private el: ElementRef, private ngZone: NgZone) {
    }

    // @Input('lazyImage') lazyImage;   // to change support to directive of: <img lazyImage="'http://www...'" ...

    @Output() loaded: EventEmitter<any> = new EventEmitter<any>();
    @Output() completed: EventEmitter<any> = new EventEmitter<any>();
    @Output() errored: EventEmitter<any> = new EventEmitter<any>();
    @Input() defaultImage: string;
    @Input() loadingImage: string;
    @Input() errorImage: string;
    @Input() retry = 5;
    @Input() delay = 1000;

    @Input()
    set urls(i_urls: Array<string>) {
        this.setUrls(i_urls);
    }

    setUrls(i_urls: Array<string>) {
        this.m_index = 0;
        this.m_urls = i_urls;
        this.loadImage(i_urls);
    }

    public resetToDefault() {
        this.setImage(this.el.nativeElement, this.defaultImage);
        this.cancel$.next({});
    }

    ngAfterViewInit() {
        this.setImage(this.el.nativeElement, this.defaultImage);
    }

    setImage(element: HTMLElement, i_url) {
        // const isImgNode = element.nodeName.toLowerCase() === 'img';
        // if (isImgNode) {
        // } else {
        //     element.style.backgroundImage = `url('${imagePath}')`;
        // }
        // tslint:disable-next-line
        (<HTMLImageElement> element).src = i_url;
        return element;
    }

    loadImage(i_urls) {
        const pollAPI$ = defer(() => {
            return new Promise((resolve, reject) => {
                const img = new Image();
                let url;
                if (i_urls[this.m_index]) {
                    url = i_urls[this.m_index];
                    this.m_index++;
                } else {
                    this.m_index = 0;
                    url = i_urls[this.m_index];
                }
                img.src = url;
                img.onload = () => {
                    resolve(url);
                };
                img.onerror = err => {
                    this.setImage(this.el.nativeElement, this.loadingImage);
                    reject(err);
                };
            });
        }).pipe(retryWhen(err => {
                return err.pipe(scan((errorCount, err2) => {
                        if (errorCount >= this.retry) {
                            throw err2;
                        }
                        // console.log(`trying to load image ${errorCount} of ${this.retry}`);
                        return errorCount + 1;
                    }, 0),
                    delay(this.delay));
            }),
            takeUntil(this.cancel$));

        pollAPI$.subscribe((v) => {
            this.setImage(this.el.nativeElement, v);
            this.loaded.emit();
        }, (e) => {
            this.setImage(this.el.nativeElement, this.errorImage);
            this.errored.emit();
            // console.error(e)
        }, () => {
            this.completed.emit({data: this.m_urls, el: this.el.nativeElement});
        });

    }
}

